import * as React from "react";

import { EmployeeTypeCategoryID, EmployeeDto } from "Api/Api";
import { Resources, useResource } from "Translations/Resources";

import { Authorized } from "Routes/Authorized";
import { EmployeeAvatar } from "Components/Shared/EmployeeAvatar";
import { PrimaryLink } from "Components/Shared/Routes/PrimaryLink";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { formatDate } from "Utils/DateUtils";
import { getEmployeeEditRight } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailPage";
import styled from "styled-components";

type Props = {
  employee: EmployeeDto;
};

const StyledWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  .avatar svg {
    width: 108px;
    height: auto;
    left: -14px;
    top: -11px;
  }
`;

const StyledRow = styled.div`
  margin-bottom: ${props => props.theme.spacing(1)}px;
`;

const StyledEmployeeName = styled.div`
  font-size: ${props => props.theme.typography.h2.fontSize}px;
  display: flex;
  align-items: center;
  font-weight: 500;
  color: ${props => props.theme.palette.secondary.main};
`;

export const EmployeeDetailBasicInfo: React.FunctionComponent<Props> = props => {
  const { employee } = props;
  const { t } = useResource();

  return (
    <>
      <StyledWrapper className={"row"}>
        <div className={"row-title avatar"}>
          <StyledFlex justifyContent={"center"}>
            <EmployeeAvatar
              firstName={employee.firstName}
              lastName={employee.lastName}
              isTheBoss={!employee.supervisorID}
              imgUrl={employee.profilePhotoUrl}
              size={"80px"}
              fontSize={"30px"}
            />
          </StyledFlex>
        </div>
        <StyledEmployeeName>{`${employee.firstName} ${employee.lastName}`}</StyledEmployeeName>
      </StyledWrapper>
      <hr />

      <StyledWrapper className={"row"}>
        <div className={"row-title"}>
          {t(Resources.HumanResources.Employees.Detail.Contact)}
        </div>
        <div>
          <StyledRow>
            <PrimaryLink link={employee.phone} type={"phone"} />
          </StyledRow>
          <div>
            <PrimaryLink link={employee.email} type={"email"} />
          </div>
        </div>
      </StyledWrapper>
      <hr />

      <StyledWrapper className={"row"}>
        <div className={"row-title"}>
          {t(Resources.HumanResources.Employees.Detail.ContactType)}
        </div>
        <div>
          <div>{employee.employeeTypeName}</div>
        </div>
      </StyledWrapper>
      <hr />

      <Authorized
        accessRights={[getEmployeeEditRight(employee.employeeTypeCategoryID)]}
      >
        {employee.birthDate && (
          <>
            <StyledWrapper className={"row"}>
              <div className={"row-title"}>
                {t(Resources.HumanResources.Employees.Detail.BirthDate)}
              </div>
              <div>
                <div>{formatDate(employee.birthDate)}</div>
              </div>
            </StyledWrapper>
            <hr />
          </>
        )}
      </Authorized>

      {employee.employeeTypeCategoryID === EmployeeTypeCategoryID.Internal && (
        <>
          <StyledWrapper className={"row"}>
            <div className={"row-title"}>
              {t(Resources.HumanResources.Employees.Detail.Anniversary)}
            </div>
            <div>
              <div>{formatDate(employee.anniversaryDate)}</div>
            </div>
          </StyledWrapper>
          <hr />
        </>
      )}

      <StyledWrapper className={"row"}>
        <StyledFlex alignItems={"center"} className={"row-title"}>
          {employee.employeeTypeCategoryID === EmployeeTypeCategoryID.Internal
            ? t(Resources.HumanResources.Employees.Detail.Supervisor)
            : "Kontaktní osoba"}
        </StyledFlex>
        <div>
          {employee.supervisorID ? (
            <StyledFlex alignItems={"center"}>
              <EmployeeAvatar
                firstName={employee.supervisorFirstName ?? ""}
                lastName={employee.supervisorLastName ?? ""}
                isTheBoss={!employee.supervisorID}
                imgUrl={employee.supervisorProfilePhotoUrl}
              />
              <div>
                {`${employee.supervisorFirstName} ${employee.supervisorLastName}`}
              </div>
            </StyledFlex>
          ) : (
            <span>-</span>
          )}
        </div>
      </StyledWrapper>
      <hr />
    </>
  );
};
